package com.iregained.site.components.sections.patients.sections

import androidx.compose.runtime.*
import com.iregained.site.components.sections.patients.styles.AccordionHeaderStyle
import com.iregained.site.components.sections.patients.styles.AccordionItemWrapperStyle
import com.iregained.site.components.sections.patients.styles.FeaturesSupportImageStyle
import com.iregained.site.components.widgets.SectionHeader
import com.iregained.site.theme.SitePalette
import com.iregained.site.theme.SubheadlineTextStyle
import com.iregained.site.theme.toSitePalette
import com.iregained.site.theme.unextractable
import com.iregained.site.utils.LocalBreakpoint
import com.iregained.site.utils.Res
import com.varabyte.kobweb.browser.dom.observers.IntersectionObserver
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.dom.Div

// UNIQUE ANIMATION: Sweep from Right
@Composable
private fun Modifier.sweepFromRight(isVisible: Boolean, delayMs: Int): Modifier {
    return this.opacity(if (isVisible) 1 else 0).translateX(if (isVisible) 0.px else 50.px).transition(
        Transition.of("opacity", 0.6.s, TransitionTimingFunction.EaseOut, delayMs.ms),
        Transition.of("transform", 0.6.s, TransitionTimingFunction.EaseOut, delayMs.ms)
    )
}

@Composable
fun PatientsFeaturesAccordionSection(data: PatientsFeaturesAccordionSectionData = defaultPatientsFeaturesAccordionSectionData) {
    val breakpoint = LocalBreakpoint.current
    val isDesktop = breakpoint >= Res.Breakpoints.LG
    val palette = ColorMode.current.toSitePalette()
    val targetId = data.sectionId?.takeIf { it.isNotBlank() } ?: "patients-features-accordion"
    var expandedIndex by remember { mutableStateOf(-1) }
    var isVisible by remember { mutableStateOf(false) }

    if (data.items.isNotEmpty()) {
        Box(
            modifier = Modifier.id(targetId).fillMaxWidth().backgroundColor(Colors.White)
                .padding(topBottom = if (isDesktop) 50.px else 25.px, leftRight = 5.percent)
                .styleModifier { property("scroll-margin-top", "100px") }, contentAlignment = Alignment.Center
        ) {
            Div(
                attrs = Modifier.fillMaxWidth().display(DisplayStyle.Flex).justifyContent(JustifyContent.Center)
                    .toAttrs {
                        ref { element ->
                            val observer = IntersectionObserver { entries, obs ->
                                entries.forEach {
                                    if (it.isIntersecting) {
                                        isVisible = true; obs.unobserve(element)
                                    }
                                }
                            }
                            observer.observe(element); onDispose { observer.disconnect() }
                        }
                    }) {
                Column(
                    modifier = Modifier.fillMaxWidth().maxWidth(1440.px),
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    if (data.headline.isNotBlank()) {
                        SectionHeader(
                            text = data.headline,
                            fontSize = 32.px,
                            lineWidthPercent = 25.percent,
                            modifier = Modifier.margin(bottom = 16.px).opacity(if (isVisible) 1 else 0)
                                .transition(Transition.of("opacity", 0.8.s)),
                            alignment = Alignment.CenterHorizontally
                        )
                    }

                    if (data.description.isNotBlank()) {
                        Div(
                            attrs = SubheadlineTextStyle.toModifier().color(palette.custom.darkText).fontSize(18.px)
                                .textAlign(TextAlign.Center).margin(bottom = 25.px).fontWeight(FontWeight.Medium)
                                .opacity(if (isVisible) 1 else 0)
                                .transition(Transition.of("opacity", 0.8.s, delay = 200.ms)).toAttrs {
                                    ref { htmlElement ->
                                        htmlElement.innerHTML = data.description; onDispose {}
                                    }
                                })
                    }

                    if (isDesktop) {
                        Row(
                            modifier = Modifier.fillMaxWidth().gap(100.px).padding(topBottom = 50.px),
                            verticalAlignment = Alignment.CenterVertically,
                            horizontalArrangement = Arrangement.SpaceBetween
                        ) {
                            Column(
                                modifier = Modifier.weight(1f).minWidth(0.px),
                                horizontalAlignment = Alignment.CenterHorizontally
                            ) {
                                if (data.imagePath.isNotBlank()) Image(
                                    src = data.imagePath,
                                    alt = data.imageAlt,
                                    modifier = FeaturesSupportImageStyle.toModifier().unextractable().fillMaxWidth()
                                        .maxHeight(500.px).borderRadius(16.px).objectFit(ObjectFit.Cover)
                                        .opacity(if (isVisible) 1 else 0)
                                        .transition(Transition.of("opacity", 1.s, delay = 400.ms))
                                )
                            }
                            Column(modifier = Modifier.weight(1.2f).minWidth(0.px)) {
                                AccordionList(
                                    data.items, palette, expandedIndex, isVisible
                                ) { clickedIndex ->
                                    expandedIndex = if (expandedIndex == clickedIndex) -1 else clickedIndex
                                }
                            }
                        }
                    } else {
                        Column(
                            modifier = Modifier.fillMaxWidth().maxWidth(800.px),
                            verticalArrangement = Arrangement.spacedBy(50.px),
                            horizontalAlignment = Alignment.CenterHorizontally
                        ) {
                            AccordionList(
                                data.items, palette, expandedIndex, isVisible
                            ) { clickedIndex ->
                                expandedIndex = if (expandedIndex == clickedIndex) -1 else clickedIndex
                            }
                            Image(
                                src = data.imagePath,
                                alt = data.imageAlt,
                                modifier = FeaturesSupportImageStyle.toModifier().fillMaxWidth().height(300.px)
                                    .borderRadius(12.px).objectFit(ObjectFit.Cover).opacity(if (isVisible) 1 else 0)
                                    .transition(Transition.of("opacity", 1.s, delay = 400.ms))
                            )
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun AccordionList(
    items: List<FeatureBenefitItem>,
    palette: SitePalette,
    expandedIndex: Int,
    isVisible: Boolean,
    onItemClick: (Int) -> Unit
) {
    Column(modifier = Modifier.fillMaxWidth()) {
        Box(modifier = Modifier.fillMaxWidth().height(1.px).backgroundColor(palette.custom.divider))
        items.forEachIndexed { index, feature ->
            key(feature.title) {
                if (feature.title.isNotBlank() && feature.description.isNotBlank()) {
                    Box(modifier = Modifier.sweepFromRight(isVisible, 300 + (index * 100))) {
                        AccordionItem(
                            feature = feature,
                            palette = palette,
                            isExpanded = expandedIndex == index,
                            onClick = { onItemClick(index) })
                    }
                }
            }
        }
    }
}

@Composable
private fun AccordionItem(feature: FeatureBenefitItem, palette: SitePalette, isExpanded: Boolean, onClick: () -> Unit) {
    Column(modifier = AccordionItemWrapperStyle.toModifier()) {
        Row(
            modifier = AccordionHeaderStyle.toModifier().onClick { onClick() },
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            SpanText(
                text = feature.title,
                modifier = Modifier.weight(1f).fontSize(20.px)
                    .fontWeight(if (isExpanded) FontWeight.Bold else FontWeight.SemiBold)
                    .color(if (isExpanded) palette.brand.primary else palette.custom.darkText)
                    .transition(Transition.of("color", 300.ms)).padding(right = 20.px)
            )
            Box(
                modifier = Modifier.size(24.px).position(Position.Relative), contentAlignment = Alignment.Center
            ) {
                Box(
                    modifier = Modifier.width(16.px).height(2.px).borderRadius(2.px)
                        .backgroundColor(if (isExpanded) palette.brand.primary else palette.custom.darkText)
                        .transition(Transition.of("background-color", 300.ms))
                )
                Box(
                    modifier = Modifier.width(2.px).height(16.px).borderRadius(2.px)
                        .backgroundColor(if (isExpanded) palette.brand.primary else palette.custom.darkText).transition(
                            Transition.of("transform", 300.ms),
                            Transition.of("opacity", 300.ms),
                            Transition.of("background-color", 300.ms)
                        ).rotate(if (isExpanded) 90.deg else 0.deg).opacity(if (isExpanded) 0.percent else 100.percent)
                )
            }
        }
        if (isExpanded) {
            Box(modifier = Modifier.fillMaxWidth().padding(left = 16.px, right = 40.px, bottom = 24.px)) {
                Div(
                    attrs = Modifier.fontSize(16.px).color(palette.custom.darkText).fontWeight(FontWeight.Medium)
                        .lineHeight(1.6)
                        .toAttrs { ref { htmlElement -> htmlElement.innerHTML = feature.description; onDispose {} } })
            }
        }
    }
}

// ==========================================
// DATA MODELS & CMS
// ==========================================

data class FeatureBenefitItem(
    val title: String, val description: String
)

/**
 * CMS IMAGE UPLOAD SPECS FOR FEATURES & BENEFITS:
 * @field imagePath:
 * - Aspect Ratio: 4:3 (Landscape) or 16:9.
 * - Recommended Dimensions: 800x600px.
 * - Format: WebP or high-quality JPEG.
 * - Content: A lifestyle shot of the device in a home setting, or a close-up of the ergonomic hand piece.
 */
data class PatientsFeaturesAccordionSectionData(
    val sectionId: String? = null,
    val headline: String,
    val description: String,
    val items: List<FeatureBenefitItem>,
    val imagePath: String,
    val imageAlt: String
)

val defaultPatientsFeaturesAccordionSectionData = PatientsFeaturesAccordionSectionData(
    sectionId = "patients-features-accordion",
    headline = "DESIGNED FOR REAL-WORLD IMPACT",
    description = "Our system bridges the gap between clinical capability and home convenience, ensuring you have the tools you need to reclaim your independence.",
    items = listOf(
        FeatureBenefitItem(
            title = "Evidence-Based Design",
            description = "Built on decades of clinical research in stroke rehabilitation and neuroplasticity to ensure effective, scientifically validated therapy."
        ), FeatureBenefitItem(
            title = "High Engagement Factor",
            description = "Gamified therapy ensures you stay motivated to complete the high number of necessary repetitions required for functional recovery."
        ), FeatureBenefitItem(
            title = "Personalized Rehabilitation",
            description = "The system intelligently adapts to your unique level of mobility, automatically adjusting difficulty and tracking your individual progress over time."
        ), FeatureBenefitItem(
            title = "Easy-to-Use",
            description = "Designed to be comfortable, non-invasive, and easy to use, whether you are in a clinical setting or adapting the system to your living space."
        ), FeatureBenefitItem(
            title = "Activities of Daily Living",
            description = "Directly translates clinical therapy exercises into the practical skills you need for everyday independence, like holding a cup or turning a key."
        ), FeatureBenefitItem(
            title = "Multi-Modal Therapy",
            description = "Features precision tracking and mechanical resistance designed specifically for the complex, intricate movements of the human hand and fingers."
        )
    ),
    // NOTE: Update with a relevant lifestyle or device image path in AppConstants
    imagePath = Res.Images.PatientsPage.FeaturesAndBenefits.FEATURE_BENEFITS,
    imageAlt = "Close up of the MyHand System's ergonomic hand interface"
)